import { Play } from '@tunasong/icons'
import { SUBDIVISION_POOL } from '@tunasong/music-lib'
import type { SubdivisionQuestion, SubdivisionVerdict } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './subdivision-feedback.module.css'

export interface SubdivisionFeedbackProps {
  question: SubdivisionQuestion
  verdict: SubdivisionVerdict
  onHearAgain: () => void
  onNext: () => void
}

/**
 * Rhythm Trainer Subdivision ID post-answer reveal. Mirrors `MeterFeedback`:
 * verdict header + reveal sentence (with picked-on-incorrect) + lexicon body
 * + Hear-again / Next actions.
 *
 * Spec: docs/superpowers/specs/2026-05-22-rhythm-trainer-v1-design.md §6.3
 */
export const SubdivisionFeedback: FC<SubdivisionFeedbackProps> = props => {
  const { verdict, onHearAgain, onNext } = props
  const correctEntry = verdict.entry
  const pickedEntry = SUBDIVISION_POOL.find(e => e.kind === verdict.picked) ?? correctEntry
  const lexicon = useTheoryCopy(correctEntry.lexiconId)
  const header = verdict.correct ? 'Correct.' : 'Not quite.'
  const lexBody = lexicon?.body ?? ''
  return (
    <section className={styles.feedback} role="status" aria-live="polite">
      <p className={`${styles.header} ${verdict.correct ? styles.correct : styles.incorrect}`}>{header}</p>
      <p className={styles.body}>
        {verdict.correct ? (
          <>That was {correctEntry.displayName.toLowerCase()}.</>
        ) : (
          <>
            That was {correctEntry.displayName.toLowerCase()}. You picked {pickedEntry.displayName.toLowerCase()}.
          </>
        )}
      </p>
      {lexBody ? <p className={styles.body}>{lexBody}</p> : null}
      <div className={styles.actions}>
        <Button variant="text" size="small" startIcon={<Play />} onClick={onHearAgain}>
          Hear it again
        </Button>
        <Button variant="contained" color="primary" onClick={onNext}>
          Next
        </Button>
      </div>
    </section>
  )
}
